/**
 * Core Indian Rummy (13-Card Rummy) Types
 *
 * All TypeScript types, interfaces, enums, and constants
 * for the Rummy game implementation.
 */

// ============================================================================
// Card Types
// ============================================================================

export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';
export type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

export interface Card {
  suit: Suit;
  rank: Rank;
  /** Unique identifier for this card instance (needed because 2 decks) */
  id: string;
  /** True for printed joker cards */
  isJoker: boolean;
  /** True if this card's rank matches the wild joker rank */
  isWildJoker: boolean;
}

export const SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];
export const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

export const RANK_VALUES: Record<Rank, number> = {
  'A': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
  '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13,
};

/** Point values for scoring (face cards = 10, Ace = 10, number cards = face value, joker = 0) */
export const CARD_POINTS: Record<Rank, number> = {
  'A': 10, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
  '8': 8, '9': 9, '10': 10, 'J': 10, 'Q': 10, 'K': 10,
};

export const SUIT_SYMBOLS: Record<Suit, string> = {
  'hearts': '♥', 'diamonds': '♦', 'clubs': '♣', 'spades': '♠',
};

export const SUIT_COLORS: Record<Suit, 'red' | 'black'> = {
  'hearts': 'red', 'diamonds': 'red', 'clubs': 'black', 'spades': 'black',
};

// ============================================================================
// Meld Types
// ============================================================================

export type MeldType = 'sequence' | 'set';

export interface Meld {
  type: MeldType;
  cards: Card[];
  /** True if this is a pure sequence (no jokers used) */
  isPure: boolean;
}

// ============================================================================
// Player Types
// ============================================================================

export type RummyDifficulty = 'easy' | 'medium' | 'hard';
export type RummyPlayerStatus = 'active' | 'declared' | 'dropped' | 'eliminated';

export interface RummyPlayerState {
  id: string;
  name: string;
  hand: Card[];
  melds: Meld[];
  isHuman: boolean;
  aiDifficulty?: RummyDifficulty;
  score: number;
  status: RummyPlayerStatus;
  /** Has the player drawn a card this turn? */
  hasDrawn: boolean;
}

export interface VisibleRummyPlayerState {
  id: string;
  name: string;
  /** Only visible for the current human player */
  hand: Card[] | null;
  melds: Meld[];
  cardCount: number;
  score: number;
  status: RummyPlayerStatus;
  hasDrawn: boolean;
}

// ============================================================================
// Game State Types
// ============================================================================

export type RummyGameStatus =
  | { type: 'waiting' }
  | { type: 'active' }
  | { type: 'roundComplete'; winnerId: string }
  | { type: 'gameOver'; winnerId: string };

export type TurnPhase = 'draw' | 'discard';

export interface RummyGameState {
  players: RummyPlayerState[];
  stock: Card[];
  discardPile: Card[];
  wildJoker: Card | null;
  currentPlayerIndex: number;
  turnPhase: TurnPhase;
  gameStatus: RummyGameStatus;
  roundNumber: number;
  /** Track discarded cards for AI */
  discardHistory: Card[];
}

export interface VisibleRummyState {
  players: VisibleRummyPlayerState[];
  stockCount: number;
  discardTop: Card | null;
  wildJoker: Card | null;
  currentPlayerIndex: number;
  turnPhase: TurnPhase;
  gameStatus: RummyGameStatus;
  roundNumber: number;
}

// ============================================================================
// Action Types
// ============================================================================

export type RummyActionType = 'draw-stock' | 'draw-discard' | 'discard' | 'declare' | 'drop' | 'sort';

export interface RummyAction {
  type: RummyActionType;
  cardId?: string;
}

export interface ActionResult {
  success: boolean;
  error?: string;
  roundComplete?: boolean;
  declarationValid?: boolean;
  winnerMelds?: Meld[];
}

// ============================================================================
// Declaration Validation
// ============================================================================

export interface DeclarationResult {
  valid: boolean;
  melds: Meld[];
  unmeldedCards: Card[];
  unmeldedPoints: number;
  hasPureSequence: boolean;
  sequenceCount: number;
  reason?: string;
}

// ============================================================================
// Game Configuration
// ============================================================================

export interface RummyGameConfig {
  playerCount: number;
  players: RummyPlayerConfig[];
}

export interface RummyPlayerConfig {
  id: string;
  name: string;
  isHuman: boolean;
  aiDifficulty?: RummyDifficulty;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface RummyGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: RummyDifficulty;
  aiOpponentCount?: number;
  playerCount?: number;
  isPrivate?: boolean;
}

// ============================================================================
// Game Result and Statistics
// ============================================================================

export interface RummyGameResult {
  gameId: string;
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  difficulty?: RummyDifficulty;
  won: boolean;
  roundsPlayed: number;
  totalPoints: number;
  duration: number;
  completedAt: number;
}

export interface RummyStatistics {
  userId?: string;
  singlePlayer: {
    easy: RummyGameStats;
    medium: RummyGameStats;
    hard: RummyGameStats;
  };
  localMultiplayer: RummyGameStats;
  onlineMultiplayer: RummyGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface RummyGameStats {
  gamesPlayed: number;
  gamesWon: number;
  winPercentage: number;
  totalPoints: number;
  bestScore: number;
  declarations: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface RummyBoardProps {
  gameState: VisibleRummyState;
  currentPlayerId: string;
  selectedCards: Set<string>;
  onCardSelect: (cardId: string) => void;
  onDrawStock: () => void;
  onDrawDiscard: () => void;
  onDiscard: (cardId: string) => void;
  onDeclare: () => void;
  onSort: () => void;
  onDrop: () => void;
  disabled: boolean;
}

export interface RummyGameModeSelectorProps {
  onStartGame: (settings: RummyGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface RummyGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const CARDS_PER_HAND = 13;
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;
export const JOKER_COUNT = 4;
export const DECK_COUNT = 2;
export const MIN_MELD_SIZE = 3;
export const MAX_SET_SIZE = 4;
export const REQUIRED_SEQUENCES = 2;
export const MAX_POINTS = 80;
